import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { STAGES } from '../lib/stages';
import '../styles/auth.css';

interface AuthSceneProps {
  eyebrow: string;
  headline: string[];
  ghost?: string;
  lede: string;
  children: ReactNode;
}

const FPS = 24;
const pad = (value: number) => String(value).padStart(2, '0');

function reducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

export default function AuthScene({ eyebrow, headline, ghost, lede, children }: AuthSceneProps) {
  const sceneRef = useRef<HTMLElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const timecodeRef = useRef<HTMLSpanElement>(null);
  const hoveredRef = useRef(false);
  const [loaded, setLoaded] = useState(false);
  const [stage, setStage] = useState(0);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setLoaded(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    if (reducedMotion()) return;
    const timer = window.setInterval(() => {
      if (!hoveredRef.current) setStage(value => (value + 1) % STAGES.length);
    }, 2600);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    const still = reducedMotion();
    const started = performance.now();
    const pointer = { x: 0.5, y: 0.4, tx: 0.5, ty: 0.4, rx: 0, ry: 0, trx: 0, try: 0 };
    let raf = 0;

    const onMove = (event: PointerEvent) => {
      const box = scene.getBoundingClientRect();
      pointer.tx = (event.clientX - box.left) / box.width;
      pointer.ty = (event.clientY - box.top) / box.height;
      const card = cardRef.current?.getBoundingClientRect();
      if (card) {
        const cx = (event.clientX - card.left) / card.width - 0.5;
        const cy = (event.clientY - card.top) / card.height - 0.5;
        const near = Math.abs(cx) < 0.9 && Math.abs(cy) < 0.9;
        pointer.trx = near ? cy * -5 : 0;
        pointer.try = near ? cx * 6 : 0;
        cardRef.current?.style.setProperty('--cx', `${(cx + 0.5) * 100}%`);
        cardRef.current?.style.setProperty('--cy', `${(cy + 0.5) * 100}%`);
      }
    };
    const onLeave = () => {
      pointer.trx = 0;
      pointer.try = 0;
    };

    const tick = (now: number) => {
      const frames = Math.floor(((now - started) / 1000) * FPS);
      if (timecodeRef.current) {
        const seconds = Math.floor(frames / FPS);
        timecodeRef.current.textContent = `${pad(Math.floor(seconds / 3600))}:${pad(Math.floor(seconds / 60) % 60)}:${pad(seconds % 60)}:${pad(frames % FPS)}`;
      }
      if (!still) {
        pointer.x += (pointer.tx - pointer.x) * 0.08;
        pointer.y += (pointer.ty - pointer.y) * 0.08;
        pointer.rx += (pointer.trx - pointer.rx) * 0.1;
        pointer.ry += (pointer.try - pointer.ry) * 0.1;
        scene.style.setProperty('--mx', `${(pointer.x * 100).toFixed(2)}%`);
        scene.style.setProperty('--my', `${(pointer.y * 100).toFixed(2)}%`);
        scene.style.setProperty('--px', (pointer.x - 0.5).toFixed(4));
        scene.style.setProperty('--py', (pointer.y - 0.5).toFixed(4));
        cardRef.current?.style.setProperty('--rx', `${pointer.rx.toFixed(3)}deg`);
        cardRef.current?.style.setProperty('--ry', `${pointer.ry.toFixed(3)}deg`);
      }
      raf = requestAnimationFrame(tick);
    };

    if (!still) {
      window.addEventListener('pointermove', onMove, { passive: true });
      document.documentElement.addEventListener('pointerleave', onLeave);
    }
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('pointermove', onMove);
      document.documentElement.removeEventListener('pointerleave', onLeave);
    };
  }, []);

  const active = STAGES[stage];

  return (
    <main ref={sceneRef} className="sa-scene" data-loaded={loaded || undefined}>
      <div className="sa-bg" aria-hidden="true">
        <div className="sa-aurora" />
        <div className="sa-spot" />
        <div className="sa-grid" />
        <div className="sa-grain" />
      </div>
      <div className="sa-letterbox sa-letterbox--top" aria-hidden="true" />
      <div className="sa-letterbox sa-letterbox--bottom" aria-hidden="true" />
      <div className="sa-frame" aria-hidden="true">
        <i />
        <i />
        <i />
        <i />
      </div>

      <header className="sa-top">
        <a className="sa-brand" href="/">
          <img src="/brand/frameon-mark.svg" alt="" width={16} height={20} />
          <span>
            Frameon
          </span>
        </a>
        <span className="sa-rec" aria-hidden="true">
          <b />
          REC <span ref={timecodeRef}>00:00:00:00</span>
        </span>
      </header>

      <section className="sa-pitch" aria-hidden="true">
        <p className="sa-mono sa-pitch-eyebrow">{eyebrow}</p>
        <h2 className="sa-display">
          {headline.map((line, index) => (
            <span key={line} className="sa-mask" style={{ '--i': index } as CSSProperties}>
              <span>{line}</span>
            </span>
          ))}
          {ghost ? (
            <span className="sa-mask sa-ghost" style={{ '--i': headline.length } as CSSProperties}>
              <span>{ghost}</span>
            </span>
          ) : null}
        </h2>
        <p className="sa-lede">{lede}</p>

        <div
          className="sa-pipeline"
          style={{ '--stages': STAGES.length } as CSSProperties}
          onPointerEnter={() => {
            hoveredRef.current = true;
          }}
          onPointerLeave={() => {
            hoveredRef.current = false;
          }}
        >
          <ol>
            {STAGES.map((item, index) => (
              <li
                key={item.id}
                data-active={index === stage || undefined}
                data-done={index < stage || undefined}
                onPointerEnter={() => setStage(index)}
              >
                <b>{item.number}</b>
                <span>{item.label}</span>
              </li>
            ))}
          </ol>
          <div className="sa-playhead" style={{ '--stage': stage } as CSSProperties} />
          <p key={active.id} className="sa-caption">
            <span className="sa-mono">{active.agent}</span>
            {active.sub}
          </p>
        </div>
      </section>

      <section className="sa-card-slot">
        <div ref={cardRef} className="sa-card">
          <div className="sa-card-glow" aria-hidden="true" />
          <div className="sa-card-body">{children}</div>
        </div>
      </section>
    </main>
  );
}
